import { useState, useEffect, useRef } from 'react';
import { Link, Link2Off, Shield, Users, UserPlus, UserCircle, MessageSquare, Trash2, Edit2, AlertTriangle, X, Lock, Eye, Globe, Search, Plus, CheckCircle, FolderKanban, Settings, ChevronDown, Download } from 'lucide-react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { useStore, type CustomButton } from '../store/useStore';
import { supabase } from '../lib/supabase';
import * as XLSX from 'xlsx';
import { compressImage } from '../lib/imageCompression';
import { Scanner } from '@yudiel/react-qr-scanner';
import { v4 as uuidv4 } from 'uuid';
import { ConfigModal } from './ConfigModal';

const TEAM_COLORS = [
  '#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#3b82f6',
  '#5f0061', '#004d4b', '#6b5900', '#0004fa', '#b30000', '#01686f', '#3d1500'
];

export function TeamConfig() {
  const { addTeam, updateTeam, deleteTeam, globalSettings, updateGlobalSettings, currentUser, categories, addMessage, messages, effectiveCategoryId } = useVisibleStore();
  const setImpersonatedTeamId = useStore(state => state.setImpersonatedTeamId);
  const { team: allTeam, sharedTeamOrganizations, addSharedTeamLink, removeSharedTeamLink, shifts, currentOrgId, points, addShift, customFieldDefinitions, teamCustomFieldValues, setTeamCustomFieldValue } = useStore();
  const isSuperAdmin = localStorage.getItem('wfm_user_email') === 'benmloukaamen@gmail.com' || 
    currentUser?.email === 'benmloukaamen@gmail.com';

  const [searchQuery, setSearchQuery] = useState('');
  const [showOpts, setShowOpts] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [customFieldFilter, setCustomFieldFilter] = useState('');
  const [ptsSearchQuery, setPtsSearchQuery] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterCustomRole, setFilterCustomRole] = useState('');

  // Wallet QR linking
  const [walletScanOpen, setWalletScanOpen] = useState(false);
  const [scannedWalletValue, setScannedWalletValue] = useState<string | null>(null);
  const [walletTeamSearch, setWalletTeamSearch] = useState('');

  const handleWalletScan = (text: string) => {
    if (!text || !text.trim()) return;
    setScannedWalletValue(text.trim());
    setWalletScanOpen(false);
    setWalletTeamSearch('');
  };

  const handleAssignWallet = (teamId: string) => {
    const s = allTeam.find(st => st.id === teamId);
    if (!s || !scannedWalletValue) return;
    if (s.linkedWalletQr && s.linkedWalletQr !== scannedWalletValue) {
      if (!window.confirm(`${s.name} a déjà un QR lié. Voulez-vous le remplacer ?`)) return;
    }
    updateTeam(teamId, { linkedWalletQr: scannedWalletValue });
    setScannedWalletValue(null);
  };

  const handleUnlinkWallet = (teamId: string, teamName: string) => {
    if (confirm(`Dissocier le QR code lié à ${teamName} ? Ce QR pourra être réattribué à un autre membre.`)) {
      updateTeam(teamId, { linkedWalletQr: null });
    }
  };


  // Include own org team AND shared team added to this organization
  const visibleTeam = allTeam.filter(s => 
    (s.orgId === currentOrgId || 
    sharedTeamOrganizations.some(link => link.teamId === s.id && link.orgId === currentOrgId)) &&
    !s.isArtist
  );

  let visibleTeamFiltered = visibleTeam;
  if (effectiveCategoryId) {
    visibleTeamFiltered = visibleTeam.filter(s => s.categoryId === effectiveCategoryId);
  }
  
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    visibleTeamFiltered = visibleTeamFiltered.filter(s =>
      s.name.toLowerCase().includes(q) ||
      (s.email && s.email.toLowerCase().includes(q)) ||
      (s.roles && s.roles.some((r: string) => r.toLowerCase().includes(q)))
    );
  }

  // Custom field filter
  if (customFieldFilter) {
    visibleTeamFiltered = visibleTeamFiltered.filter(s => {
      const cv = teamCustomFieldValues.find(
        v => v.teamId === s.id && v.fieldId === customFieldFilter
      );
      return cv && cv.value;
    });
  }

  // Category filter
  if (filterCategoryId) {
    visibleTeamFiltered = visibleTeamFiltered.filter(s => s.categoryId === filterCategoryId);
  }

  // Primary role filter
  if (filterRole) {
    visibleTeamFiltered = visibleTeamFiltered.filter(s => (s.role || '') === filterRole);
  }

  // Custom role filter
  if (filterCustomRole) {
    visibleTeamFiltered = visibleTeamFiltered.filter(s => s.roles?.includes(filterCustomRole));
  }

  // Compute distinct values for filter dropdowns (from ALL visible team, before other filters)
  const distinctRoles = [...new Set(visibleTeam.map(s => s.role).filter(Boolean))].sort() as string[];
  const distinctCustomRoles = [...new Set(visibleTeam.flatMap(s => s.roles || []).filter(Boolean))].sort();

  // Wallet team filter
  const walletFiltered = !walletTeamSearch
    ? visibleTeam
    : visibleTeam.filter(s => s.name.toLowerCase().includes(walletTeamSearch.toLowerCase()) || (s.email || '').toLowerCase().includes(walletTeamSearch.toLowerCase()));

  const defaultCategory = effectiveCategoryId ? effectiveCategoryId : (categories[0]?.id || '');
  // roles[] = custom/optional roles only; primary role = category name (derived)
  const defaultTeam = { name: '', categoryId: defaultCategory, role: '', roles: [] as string[], email: '', phone: '', color: TEAM_COLORS[0], photo: '', customButtons: [] as CustomButton[], excludeFromMagicFill: false, specialties: [] as string[], isCategorySupervisor: false };
  const [formData, setFormData] = useState<{ id?: string; categoryId?: string; name: string; role?: string; roles: string[]; email: string; phone: string; color: string; photo?: string; customButtons?: CustomButton[]; excludeFromMagicFill?: boolean; specialties?: string[]; isCategorySupervisor?: boolean }>(defaultTeam);
  const [customRoleInput, setCustomRoleInput] = useState('');

  // Freelancer Discovery states
  const [findFreelancersOpen, setFindFreelancersOpen] = useState(false);
  const [freelanceSearch, setFreelanceSearch] = useState('');
  const [freelanceSpecialty, setFreelanceSpecialty] = useState('');
  const [freelanceMaxRate, setFreelanceMaxRate] = useState<number>(1000);
  const [freelanceZone, setFreelanceZone] = useState('');

  const [linkModal, setLinkModal] = useState<{ show: boolean; teamName: string; link: string } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{ message: string, onConfirm: () => void } | null>(null);
  const [promptModal, setPromptModal] = useState<{ title: string, placeholder?: string, onConfirm: (val: string) => void } | null>(null);
  const [promptVal, setPromptVal] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [initialFormData, setInitialFormData] = useState(defaultTeam);
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteCategoryId, setInviteCategoryId] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [customFieldDraft, setCustomFieldDraft] = useState<Record<string, string>>({});

  // Bulk selection
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(new Set());
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [showBulkCustomRoleModal, setShowBulkCustomRoleModal] = useState(false);
  const [bulkCustomRoleAction, setBulkCustomRoleAction] = useState<'add' | 'remove'>('add');
  const [bulkCustomRoleValue, setBulkCustomRoleValue] = useState('');

  const toggleTeamSelection = (id: string) => {
    setSelectedTeamIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const allIds = visibleTeamFiltered.map(s => s.id);
    const allSelected = allIds.every(id => selectedTeamIds.has(id));
    if (allSelected) {
      setSelectedTeamIds(new Set());
    } else {
      setSelectedTeamIds(new Set(allIds));
    }
  };

  const clearSelection = () => setSelectedTeamIds(new Set());

  const bulkDelete = () => {
    const count = selectedTeamIds.size;
    if (!window.confirm(`Supprimer définitivement ${count} membre(s) ? Cette action est irréversible.`)) return;
    selectedTeamIds.forEach(id => deleteTeam(id));
    clearSelection();
  };

  const bulkApprove = () => {
    const pendingIds = [...selectedTeamIds].filter(id => {
      const s = visibleTeam.find(st => st.id === id);
      return s && s.status === 'pending';
    });
    if (pendingIds.length === 0) return;
    if (!window.confirm(`Approuver ${pendingIds.length} membre(s) en attente ?`)) return;
    pendingIds.forEach(id => updateTeam(id, { status: 'approved' }));
    clearSelection();
  };

  const bulkSendMessage = () => {
    const ids = [...selectedTeamIds];
    setPromptModal({
      title: `Envoyer un message à ${ids.length} membre(s)`,
      placeholder: 'Votre message...',
      onConfirm: (val: string) => {
        if (!val.trim()) return;
        const senderLabel = currentUser.email || 'Admin';
        ids.forEach(teamId => addMessage(teamId, val.trim(), senderLabel));
        clearSelection();
      }
    });
    setPromptVal('');
  };

  const bulkReassignCategory = () => {
    if (!bulkCategoryId) return;
    selectedTeamIds.forEach(id => updateTeam(id, { categoryId: bulkCategoryId }));
    setShowBulkCategoryModal(false);
    setBulkCategoryId('');
    clearSelection();
  };

  const bulkManageCustomRole = () => {
    const role = bulkCustomRoleValue.trim();
    if (!role) return;

    if (bulkCustomRoleAction === 'add') {
      // Add role to all selected members (avoid duplicates)
      selectedTeamIds.forEach(id => {
        const member = visibleTeam.find(s => s.id === id);
        if (!member) return;
        const currentRoles = member.roles || [];
        if (!currentRoles.includes(role)) {
          updateTeam(id, { roles: [...currentRoles, role] });
        }
      });
    } else {
      // Remove role from all selected members
      selectedTeamIds.forEach(id => {
        const member = visibleTeam.find(s => s.id === id);
        if (!member) return;
        const currentRoles = member.roles || [];
        if (currentRoles.includes(role)) {
          updateTeam(id, { roles: currentRoles.filter(r => r !== role) });
        }
      });
    }

    setShowBulkCustomRoleModal(false);
    setBulkCustomRoleValue('');
    clearSelection();
  };

  // Compute distinct custom roles from selected members (for remove suggestions)
  const distinctSelectedCustomRoles = [...new Set(
    [...selectedTeamIds].flatMap(id => {
      const m = visibleTeam.find(s => s.id === id);
      return m?.roles || [];
    }).filter(Boolean)
  )].sort();

  const hasSelection = selectedTeamIds.size > 0;
  const hasPendingSelected = [...selectedTeamIds].some(id => {
    const s = visibleTeam.find(st => st.id === id);
    return s && s.status === 'pending';
  });
  const allSelected = visibleTeamFiltered.length > 0 && visibleTeamFiltered.every(s => selectedTeamIds.has(s.id));

  // Filter custom field definitions for the current org
  const orgCustomFields = customFieldDefinitions
    .filter(f => f.orgId === currentOrgId)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const exportToXlsx = () => {
    const customFieldDefs = customFieldDefinitions
      .filter(f => f.orgId === currentOrgId)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Headers: standard fields + custom fields
    const standardHeaders = [
      'Nom complet', 'Email', 'Téléphone', 'Catégorie', 'Rôle principal',
      'Rôles personnalisés', 'Statut', 'Superviseur catégorie',
      'Exclu Magic Fill', 'Distributeur', 'Scan autorisé',
      'Distribution directe', 'Check-in autorisé', 'Lier wallet autorisé',
      'Vue équipe autorisée', 'ID'
    ];
    const customHeaders = customFieldDefs.map(f => f.label);
    const headers = [...standardHeaders, ...customHeaders];

    // Build rows from visibleTeamFiltered
    const rows = visibleTeamFiltered.map(s => {
      const cat = categories.find(c => c.id === s.categoryId);
      const standardValues = [
        s.name || '',
        s.email || '',
        s.phone || '',
        cat?.name || '',
        s.role || '',
        (s.roles || []).join(', '),
        s.status || '',
        s.isCategorySupervisor ? 'Oui' : 'Non',
        s.excludeFromMagicFill ? 'Oui' : 'Non',
        s.isDistributor ? 'Oui' : 'Non',
        s.distAllowScan ? 'Oui' : 'Non',
        s.distAllowDirect ? 'Oui' : 'Non',
        s.distAllowCheckin ? 'Oui' : 'Non',
        s.distAllowLinkWallet ? 'Oui' : 'Non',
        s.distAllowView ? 'Oui' : 'Non',
        s.id
      ];
      const customValues = customFieldDefs.map(f => {
        const cv = teamCustomFieldValues.find(
          v => v.teamId === s.id && v.fieldId === f.id
        );
        return cv?.value || '';
      });
      return [...standardValues, ...customValues];
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Auto-set column widths
    const colWidths = headers.map((h, i) => {
      const maxLen = Math.max(
        h.length,
        ...rows.map(r => String(r[i] || '').length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
    });
    ws['!cols'] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team');

    const timestamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `export_team_${timestamp}.xlsx`);
  };

  const handleCustomFieldChange = (fieldId: string, value: string) => {
    setCustomFieldDraft(prev => ({ ...prev, [fieldId]: value }));
  };

  const generateTeamLink = async (s: any) => {
    const token = uuidv4();
    updateTeam(s.id, { teamToken: token });
    const link = `${window.location.origin}/?team_token=${token}`;
    setLinkModal({ show: true, teamName: s.name, link });
  };

  const revokeTeamToken = async (s: any) => {
    if (!confirm(`Révoquer l'accès de "${s.name}" ? Le lien actuel ne fonctionnera plus.`)) return;
    updateTeam(s.id, { teamToken: undefined });
  };

  const showExistingTeamLink = (s: any) => {
    const link = `${window.location.origin}/?team_token=${s.teamToken}`;
    setLinkModal({ show: true, teamName: s.name, link });
  };

  const handleSave = async () => {
    if (!formData.name) return;
    const categoryIdToSave = effectiveCategoryId ? effectiveCategoryId : formData.categoryId;
    if (formData.id) {
      updateTeam(formData.id, { ...formData, categoryId: categoryIdToSave });
      // Save custom field values for existing team
      const fieldEntries = Object.entries(customFieldDraft);
      if (fieldEntries.length > 0) {
        fieldEntries.forEach(([fieldId, value]) => {
          setTeamCustomFieldValue(formData.id!, fieldId, value);
        });
      }
      setCustomFieldDraft({});
      setFormData(defaultTeam);
      setInitialFormData(defaultTeam);
      setModalOpen(false);
    } else {
      if (formData.email && formData.email.includes('@')) {
        const cleanEmail = formData.email.toLowerCase().trim();
        try {
          const { data: existingTeam } = await supabase
            .from('staff')
            .select('*')
            .eq('email', cleanEmail)
            .eq('email_confirmed', true)
            .is('deleted_at', null)
            .limit(1);

          if (existingTeam && existingTeam.length > 0) {
            const match = existingTeam[0];
            
            // Check if already linked to this organization
            const isAlreadyLinked = sharedTeamOrganizations.some(
              link => link.teamId === match.id && link.orgId === currentOrgId
            ) || match.org_id === currentOrgId;

            if (isAlreadyLinked) {
              alert("Cet intervenant est déjà membre ou lié à votre organisation !");
              return;
            }

            if (points.length === 0) {
              alert("Veuillez d'abord créer au moins un point opérationnel (Lieu) dans votre organisation avant d'ajouter un compte autonome.");
              return;
            }

            // Link them as shared team (autonomous account)
            const linkError = await addSharedTeamLink(match.id, currentOrgId || '');
            if (linkError) {
              alert(`Impossible de lier ${match.name}.\n\nErreur : ${linkError}`);
              return;
            }

            // Create a proposal shift (Missions & Prestations Proposées)
            const firstPointId = points[0].id;
            const orgName = useStore.getState().organizations.find(o => o.id === currentOrgId)?.name || "Organisation";
            
            const tomorrowStr = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
            
            const newShift = {
              teamId: match.id,
              pointId: firstPointId,
              start: `${tomorrowStr}T09:00:00Z`,
              end: `${tomorrowStr}T17:00:00Z`,
              description: `Demande d'intégration de l'organisation : ${orgName}`,
              shiftType: 'standard' as const,
              proposedAmount: match.indicative_daily_rate || 0,
              proposedCurrency: match.proposed_currency || 'TND',
              agreementStatus: 'pending_agreement'
            };
            
            await addShift(newShift);
            
            alert(`Compte autonome trouvé ! ${match.name} a été lié à votre organisation. Une demande de validation a été envoyée et apparaîtra dans son onglet 'Missions & Prestations Proposées'.`);
            
            setFormData(defaultTeam);
            setInitialFormData(defaultTeam);
            setModalOpen(false);
            return;
          }
        } catch (err) {
          console.error("Error checking autonomous team:", err);
        }
      }
      
      addTeam({ ...formData, categoryId: categoryIdToSave });
      setFormData(defaultTeam);
      setInitialFormData(defaultTeam);
      setModalOpen(false);
    }
  };

  const openNew = () => {
    setFormData(defaultTeam);
    setInitialFormData(defaultTeam);
    setPtsSearchQuery('');
    setCustomFieldDraft({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setPtsSearchQuery('');
  };
  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialFormData);

  // Auto-open modal when navigated from Setup Guide (mobile)
  useEffect(() => {
    const flag = sessionStorage.getItem('guide_auto_open');
    if (flag === 'new-team') {
      sessionStorage.removeItem('guide_auto_open');
      setTimeout(() => openNew(), 150);
    }
  }, []);

  const handleEdit = (s: any) => {
    if (s.emailConfirmed && !isSuperAdmin && s.id !== currentUser.teamId) {
      alert("Ce compte est vérifié. Seul le superadmin peut modifier ces informations.");
      return;
    }
    setFormData({
      id: s.id,
      categoryId: s.categoryId || defaultCategory,
      name: s.name,
      role: s.role || '',
      // roles = custom roles only (category is the primary role, derived from categoryId)
      roles: (s.roles || []).filter((r: string) => {
        // filter out any legacy predefined roles that are now replaced by category
        const catNames = categories.map(c => c.name);
        return !catNames.includes(r) && r !== 'Global' && r !== 'Parking Only' && r !== 'Cashless Only' && r !== 'Check-in Only';
      }),
      email: s.email || '',
      phone: s.phone || '',
      color: s.color || TEAM_COLORS[0],
      customButtons: s.customButtons || [],
      excludeFromMagicFill: s.excludeFromMagicFill || false,
      isCategorySupervisor: s.isCategorySupervisor || false,
      specialties: s.specialties || [],
    });
    setCustomRoleInput('');
    setPtsSearchQuery('');
    setCustomFieldDraft({});
    // Note: setInitialFormData needs the new data, not the stale formData state
    setModalOpen(true);
  };

  const addCustomRole = () => {
    const role = customRoleInput.trim();
    if (!role || (formData.roles || []).includes(role)) return;
    setFormData({ ...formData, roles: [...(formData.roles || []), role] });
    setCustomRoleInput('');
  };

  const removeCustomRole = (role: string) => {
    setFormData({ ...formData, roles: (formData.roles || []).filter(r => r !== role) });
  };

  return (
    <div className="p-3.5 sm:p-6 md:p-8 h-full flex flex-col overflow-hidden relative">
      {/* Background glow */}
      <div className="absolute bottom-[-10%] left-[20%] w-[600px] h-[600px] bg-emerald-600/10 rounded-full blur-[150px] pointer-events-none"></div>

      <div className="mb-6 sm:mb-8 flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent flex items-center gap-2 sm:gap-3">
            <Users className="text-emerald-400 w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
            <span className="truncate">Team Management</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm mt-1 sm:mt-2 max-w-2xl leading-relaxed">
            Configurez les membres de l'équipe, leurs rôles et autorisations d'accès.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Options dropdown */}
          <div className="relative" style={{ zIndex: 50 }}>
            <button
              onClick={() => setShowOpts(!showOpts)}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#14161B] hover:bg-[#1A1D23] border border-[#2D3139] hover:border-emerald-500/30 text-slate-300 hover:text-white font-semibold text-xs rounded-xl transition-all h-[42px]"
            >
              <Settings className="w-4 h-4" />
              <span>Options</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showOpts ? 'rotate-180' : ''}`} />
            </button>
            {showOpts && (
              <div className="absolute left-0 sm:left-auto sm:right-0 mt-1 w-52 bg-[#14161B] border border-[#2D3139] rounded-xl shadow-2xl py-1">
                <button onClick={() => { openNew(); setShowOpts(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-emerald-500/10 hover:text-emerald-400 transition-colors">
                  <UserPlus className="w-3.5 h-3.5" /> Ajouter Team
                </button>
                <button onClick={() => { exportToXlsx(); setShowOpts(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-amber-500/10 hover:text-amber-400 transition-colors">
                  <Download className="w-3.5 h-3.5" /> Exporter XLSX
                </button>
                <button onClick={() => { setInviteCategoryId(defaultCategory); setInviteModal(true); setShowOpts(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-indigo-500/10 hover:text-indigo-400 transition-colors">
                  <Link className="w-3.5 h-3.5" /> Invitation
                </button>
                <button onClick={() => { setFindFreelancersOpen(true); setShowOpts(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-purple-500/10 hover:text-purple-400 transition-colors">
                  <Globe className="w-3.5 h-3.5" /> Trouver Teams 🌐
                </button>
                <button onClick={() => { setWalletScanOpen(true); setShowOpts(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-300 hover:bg-purple-500/10 hover:text-purple-400 transition-colors">
                  <Link className="w-3.5 h-3.5" /> Link Wallet
                </button>
                <hr className="border-[#2D3139] my-1" />
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="text-[10px] text-slate-500">Repos min.</span>
                  <div className="flex items-center gap-1">
                    <input type="number" min="1" max="48"
                      className="w-9 bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-1.5 py-0.5 text-emerald-400 font-bold text-center text-[10px] focus:outline-none"
                      value={globalSettings?.minRestTimeHours || 8}
                      onChange={(e) => updateGlobalSettings({ minRestTimeHours: Number(e.target.value) || 8 })}
                    />
                    <span className="text-slate-500 text-[10px]">h</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-col flex-1 min-h-0 relative z-10">
        
        {/* Team Form Modal */}
        <ConfigModal
          isOpen={modalOpen}
          onClose={closeModal}
          title={formData.id ? 'Modifier Team' : 'Ajouter Team'}
          subtitle={formData.id ? "Mettre à jour les informations du membre" : "Ajouter un nouveau membre à l'équipe"}
          icon={formData.id ? <Edit2 className="w-5 h-5" /> : <UserPlus className="w-5 h-5" />}
          isDirty={isDirty}
          wide
        >
          <div className="p-6 space-y-5">
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const compressed = await compressImage(file);
              setFormData({ ...formData, photo: compressed });
            }} />

            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Nom complet</label>
              <input autoFocus value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: John Doe"
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600" />
            </div>

            {/* Category (admin only) */}
            {currentUser.role === 'admin' && (
              <div>
                <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Catégorie</label>
                <select value={formData.categoryId || ''} onChange={e => setFormData({ ...formData, categoryId: e.target.value })}
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all appearance-none">
                  <option value="">-- Sélectionner une catégorie --</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            {/* Email + Phone */}
            {(() => {
              const isSharedTeam = formData.id && allTeam.find(s => s.id === formData.id)?.orgId !== currentOrgId;
              const hasAgreedShift = formData.id && shifts.some(sh => sh.teamId === formData.id && sh.orgId === currentOrgId && (sh.agreementStatus === 'agreed' || sh.agreementStatus === 'approved'));
              const showContacts = !isSharedTeam || hasAgreedShift;

              return (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Email</label>
                    <input 
                      disabled={!!isSharedTeam}
                      value={showContacts ? formData.email : "🔒 Masqué (En attente d'affectation)"} 
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@..." 
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] disabled:opacity-60 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all placeholder-slate-600" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Téléphone</label>
                    <input 
                      disabled={!!isSharedTeam}
                      value={showContacts ? formData.phone : "🔒 Masqué (En attente d'affectation)"} 
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+33 6..." 
                      className="w-full bg-[#0A0B0E] border border-[#2D3139] disabled:opacity-60 rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-emerald-500 transition-all placeholder-slate-600" 
                    />
                  </div>
                </div>
              );
            })()}

            {/* Primary role (editable, defaults to category name) */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Rôle principal</label>
              <input
                type="text"
                value={formData.role || ''}
                onChange={e => setFormData({ ...formData, role: e.target.value })}
                placeholder={categories.find(c => c.id === (formData.categoryId || defaultCategory))?.name || 'Rôle principal'}
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 transition-all placeholder-slate-600"
              />
              <p className="text-[10px] text-slate-500 mt-1">Laisser vide pour utiliser le nom de la catégorie</p>
            </div>

            {/* Custom roles */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Rôle(s) personnalisé(s)</label>
              {(formData.roles || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {(formData.roles || []).map(r => (
                    <span key={r} className="flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-full text-xs font-medium">
                      {r}
                      <button onClick={() => removeCustomRole(r)} className="hover:text-amber-200 bg-amber-500/20 rounded-full p-0.5"><X className="w-3 h-3"/></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input value={customRoleInput} onChange={e => setCustomRoleInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomRole(); } }}
                  placeholder="Ex: VIP Host, Runner..."
                  className="flex-1 bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2 text-white text-sm focus:outline-none focus:border-amber-500/50 transition-all" />
                <button onClick={addCustomRole} disabled={!customRoleInput.trim()}
                  className="px-4 py-2 bg-amber-600/20 hover:bg-amber-600/40 disabled:opacity-30 text-amber-400 border border-amber-500/30 rounded-xl text-sm font-medium transition-colors">Ajouter</button>
              </div>
            </div>

            {/* Specialties (Points restricted) */}
            <div className="pt-2">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5 text-amber-500" /> Compétences Certifiées
                </label>
                <div className="relative w-full sm:w-48">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                    <Search className="w-3 h-3 text-slate-500" />
                  </span>
                  <input
                    type="text"
                    value={ptsSearchQuery}
                    onChange={e => setPtsSearchQuery(e.target.value)}
                    placeholder="Chercher un point..."
                    className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg pl-7 pr-7 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 transition-all"
                  />
                  {ptsSearchQuery && (
                    <button onClick={() => setPtsSearchQuery('')} className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-500 hover:text-white">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139] custom-scrollbar">
                {useVisibleStore().points
                  .filter(pt => pt.name.toLowerCase().includes(ptsSearchQuery.toLowerCase()))
                  .map(pt => {
                  const isChecked = (formData.specialties || []).includes(pt.id);
                  return (
                    <label key={pt.id} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all cursor-pointer ${isChecked ? 'bg-amber-500/10 border-amber-500/30 text-amber-400 shadow-sm' : 'border-[#1F2937] text-slate-400 hover:border-slate-700'}`}>
                      <input 
                        type="checkbox" 
                        checked={isChecked}
                        onChange={(e) => {
                          const current = formData.specialties || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, specialties: [...current, pt.id] });
                          } else {
                            setFormData({ ...formData, specialties: current.filter(id => id !== pt.id) });
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-amber-500 focus:ring-amber-500/20"
                      />
                      <span className="text-xs font-medium truncate">{pt.name}</span>
                      {pt.isRestricted && <Lock className="w-3 h-3 ml-auto opacity-50" />}
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-slate-500 mt-2 italic">Cochez les points spécifiques pour lesquels cet agent est habilité.</p>
            </div>

            {/* Advanced Settings */}
            <div className="pt-2">
              <label className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer ${formData.excludeFromMagicFill ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-[#0A0B0E] border-[#2D3139] hover:border-slate-700'}`}>
                <input type="checkbox" checked={formData.excludeFromMagicFill} onChange={e => setFormData({...formData, excludeFromMagicFill: e.target.checked})} className="sr-only" />
                <div className={`w-10 h-6 rounded-full transition-colors relative ${formData.excludeFromMagicFill ? 'bg-indigo-600' : 'bg-slate-700'}`}>
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.excludeFromMagicFill ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs font-bold uppercase tracking-wide ${formData.excludeFromMagicFill ? 'text-indigo-400' : 'text-slate-400'}`}>Exclure du Magic Fill ✨</span>
                  <span className="text-[10px] text-slate-500">Ne sera pas assigné automatiquement</span>
                </div>
              </label>

              <label className={`flex items-center gap-3 p-4 rounded-xl border transition-all cursor-pointer mt-2 ${formData.isCategorySupervisor ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-[#0A0B0E] border-[#2D3139] hover:border-slate-700'}`}>
                <input type="checkbox" checked={formData.isCategorySupervisor} onChange={e => setFormData({...formData, isCategorySupervisor: e.target.checked})} className="sr-only" />
                <div className={`w-10 h-6 rounded-full transition-colors relative ${formData.isCategorySupervisor ? 'bg-emerald-600' : 'bg-slate-700'}`}>
                  <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.isCategorySupervisor ? 'translate-x-4' : 'translate-x-0'}`} />
                </div>
                <div className="flex flex-col">
                  <span className={`text-xs font-bold uppercase tracking-wide ${formData.isCategorySupervisor ? 'text-emerald-400' : 'text-slate-400'}`}>Superviseur de catégorie 👁️</span>
                  <span className="text-[10px] text-slate-500">Voit tous les shifts de sa catégorie (pas seulement les siens)</span>
                </div>
              </label>
            </div>

            {/* Custom Fields */}
            {orgCustomFields.length > 0 && (
              <div className="pt-2 border-t border-[#2D3139]/50">
                <label className="block text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider">
                  Champs personnalisés
                </label>
                <div className="space-y-3">
                  {orgCustomFields.map(field => {
                    const existingValue = formData.id
                      ? teamCustomFieldValues.find(v => v.teamId === formData.id && v.fieldId === field.id)?.value
                      : null;
                    const draftValue = customFieldDraft[field.id] !== undefined ? customFieldDraft[field.id] : (existingValue || '');
                    return (
                      <div key={field.id} className="w-full">
                        <label className="block text-xs text-slate-400 mb-1.5">
                          {field.label}
                          {field.required && <span className="text-red-400 ml-1">*</span>}
                        </label>
                        {field.fieldType === 'textbox' && (
                          <input type="text"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-600 text-sm"
                            placeholder={field.label}
                            value={draftValue}
                            onChange={e => handleCustomFieldChange(field.id, e.target.value)}
                          />
                        )}
                        {field.fieldType === 'number' && (
                          <input type="number"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-emerald-500 placeholder-slate-600 text-sm"
                            placeholder={field.label}
                            value={draftValue}
                            onChange={e => handleCustomFieldChange(field.id, e.target.value)}
                          />
                        )}
                        {field.fieldType === 'date' && (
                          <input type="date"
                            className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-emerald-500 text-sm"
                            value={draftValue}
                            onChange={e => handleCustomFieldChange(field.id, e.target.value)}
                          />
                        )}
                        {field.fieldType === 'radio' && (
                          <div className="grid grid-cols-2 gap-2 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139]">
                            {field.options.map(opt => (
                              <label key={opt}
                                className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-xs ${
                                  draftValue === opt
                                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                                    : 'border-[#2D3139] text-slate-400 hover:border-slate-600'
                                }`}
                              >
                                <input type="radio"
                                  name={`cf_${field.id}`}
                                  value={opt}
                                  checked={draftValue === opt}
                                  onChange={e => handleCustomFieldChange(field.id, e.target.value)}
                                  className="sr-only"
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {field.fieldType === 'checkbox' && (
                          <label className="flex items-center gap-3 p-3 bg-[#0A0B0E] rounded-xl border border-[#2D3139] cursor-pointer hover:border-slate-700 transition-all">
                            <input type="checkbox"
                              checked={draftValue === 'true'}
                              onChange={e => handleCustomFieldChange(field.id, e.target.checked ? 'true' : 'false')}
                              className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500/20"
                            />
                            <span className="text-xs text-slate-400">{field.label}</span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Buttons */}
            <div className="pt-2 border-t border-[#2D3139]/50">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Boutons personnalisés (max 3)</label>
                {(formData.customButtons || []).length < 3 && (
                  <button onClick={() => setFormData({ ...formData, customButtons: [...(formData.customButtons || []), { label: '', url: '', color: 'indigo' }] })}
                    className="text-xs font-bold text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 hover:bg-indigo-500/20 px-2 py-1 rounded-lg transition-colors">+ Ajouter</button>
                )}
              </div>
              <div className="space-y-3">
                {(formData.customButtons || []).map((btn, idx) => (
                  <div key={idx} className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-3 space-y-2">
                    <div className="flex gap-2">
                      <input value={btn.label} onChange={e => { const btns = [...(formData.customButtons || [])]; btns[idx] = { ...btns[idx], label: e.target.value }; setFormData({ ...formData, customButtons: btns }); }}
                        placeholder="Texte du bouton" className="w-1/3 bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 placeholder-slate-600" />
                      <input value={btn.url} onChange={e => { const btns = [...(formData.customButtons || [])]; btns[idx] = { ...btns[idx], url: e.target.value }; setFormData({ ...formData, customButtons: btns }); }}
                        placeholder="https://..." className="flex-1 bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-indigo-500 placeholder-slate-600" />
                      <button onClick={() => { const btns = (formData.customButtons || []).filter((_, i) => i !== idx); setFormData({ ...formData, customButtons: btns }); }}
                        className="px-2 py-1 text-red-400 hover:bg-red-500/10 rounded-lg text-sm">X</button>
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <span className="text-[10px] text-slate-500 uppercase tracking-wider w-full mb-1">Couleur:</span>
                      {(['indigo', 'emerald', 'amber', 'rose', 'sky', 'purple'] as const).map(c => (
                        <button key={c} onClick={() => { const btns = [...(formData.customButtons || [])]; btns[idx] = { ...btns[idx], color: c }; setFormData({ ...formData, customButtons: btns }); }}
                          className={`w-5 h-5 rounded-full border-2 transition-all ${btn.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                          style={{ backgroundColor: { indigo: '#6366f1', emerald: '#10b981', amber: '#f59e0b', rose: '#f43f5e', sky: '#0ea5e9', purple: '#a855f7' }[c] }} title={c} />
                      ))}
                    </div>
                  </div>
                ))}
                {(formData.customButtons || []).length === 0 && <p className="text-xs text-slate-600 italic">Aucun bouton</p>}
              </div>
            </div>

            {/* Color */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Couleur d'identification</label>
              <div className="flex flex-wrap gap-2.5 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139]">
                {TEAM_COLORS.map(c => (
                  <button key={c} onClick={() => setFormData({ ...formData, color: c })}
                    className={`w-7 h-7 rounded-full border-2 transition-all shadow-sm ${formData.color === c ? 'border-white scale-125 shadow-md z-10' : 'border-transparent hover:scale-110'}`}
                    style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>

            {/* Photo */}
            <div>
              <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Photo de profil</label>
              <div className="flex items-center gap-4 bg-[#0A0B0E] p-3 rounded-xl border border-[#2D3139]">
                {formData.photo
                  ? <img src={formData.photo} className="w-12 h-12 rounded-full object-cover border-2 border-[#2D3139] shadow-md" alt="" />
                  : <div className="w-12 h-12 rounded-full bg-[#14161B] border border-[#2D3139] flex items-center justify-center text-slate-500"><UserCircle className="w-6 h-6" /></div>
                }
                <div className="flex gap-2">
                  <button onClick={() => fileInputRef.current?.click()} className="text-xs bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 border border-indigo-500/30 px-3 py-1.5 rounded-lg font-medium transition-colors">
                    {formData.photo ? 'Changer' : 'Parcourir...'}
                  </button>
                  {formData.photo && <button onClick={() => setFormData({ ...formData, photo: '' })} className="text-xs text-red-400 hover:bg-red-500/10 px-3 py-1.5 rounded-lg font-medium transition-colors">Supprimer</button>}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2 border-t border-[#2D3139]/50">
              <button onClick={closeModal} className="flex-1 py-3 border border-[#2D3139] hover:bg-[#1A1D23] text-white rounded-xl transition-all flex items-center justify-center gap-2 font-medium">
                <X className="w-4 h-4" /> Annuler
              </button>
              <button onClick={handleSave} disabled={!formData.name.trim()}
                className={`flex-1 py-3 disabled:opacity-50 text-white rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${formData.id ? 'bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 shadow-amber-500/20' : 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 shadow-emerald-500/20'}`}>
                {formData.id ? <Edit2 className="w-4 h-4"/> : <UserPlus className="w-4 h-4"/>}
                {formData.id ? 'Mettre à jour' : 'Ajouter au team'}
              </button>
            </div>
          </div>
        </ConfigModal>

        {/* Team list */}
        <div className="flex-1 bg-transparent sm:bg-[#14161B]/50 backdrop-blur-none sm:backdrop-blur-sm border-0 sm:border border-[#2D3139] rounded-none sm:rounded-2xl overflow-y-auto p-0 sm:p-6 custom-scrollbar relative shadow-none sm:shadow-inner z-10">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sticky top-0 bg-[#14161B] sm:bg-[#14161B]/90 backdrop-blur-md p-2 -mt-2 sm:-mt-2 sm:-mx-2 rounded-lg z-20 border-b border-[#2D3139]/30 pb-3">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500/20"
                />
              </label>
              <h3 className="font-semibold text-lg text-white">Membres Actuels</h3>
              <span className="bg-emerald-500/20 text-emerald-400 text-xs font-bold px-3 py-1 rounded-full border border-emerald-500/30">
                {hasSelection ? `${selectedTeamIds.size} / ${visibleTeamFiltered.length}` : `${visibleTeamFiltered.length} membres`}
              </span>
            </div>
            
            {/* Filters toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                showFilters || searchQuery || filterCategoryId || filterRole || filterCustomRole || customFieldFilter
                  ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-400'
                  : 'bg-[#14161B] border-[#2D3139] text-slate-400 hover:text-white'
              }`}
            >
              <Search className="w-3.5 h-3.5" />
              Filtres
              {(searchQuery || filterCategoryId || filterRole || filterCustomRole || customFieldFilter) && (
                <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full" />
              )}
              <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>

            {/* Collapsible filter row */}
            {showFilters && (
            <div className="flex flex-wrap items-center gap-2 w-full">
            {/* Search Input */}
            <div className="relative w-full sm:w-56">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="w-4 h-4 text-slate-400" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher par nom, rôle..."
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-8 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/50 transition-all font-medium"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Category Filter */}
            <select
              value={filterCategoryId}
              onChange={e => setFilterCategoryId(e.target.value)}
              className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500 appearance-none"
            >
              <option value="">Toutes catégories</option>
              {categories.filter(c => c.orgId === currentOrgId).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            {/* Primary Role Filter */}
            {distinctRoles.length > 0 && (
              <select
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500 appearance-none"
              >
                <option value="">Tous rôles</option>
                {distinctRoles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            )}

            {/* Custom Role Filter */}
            {distinctCustomRoles.length > 0 && (
              <select
                value={filterCustomRole}
                onChange={e => setFilterCustomRole(e.target.value)}
                className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500 appearance-none"
              >
                <option value="">Tous rôles perso.</option>
                {distinctCustomRoles.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            )}

            {/* Custom Field Filter */}
            {orgCustomFields.length > 0 && (
              <select
                value={customFieldFilter}
                onChange={e => setCustomFieldFilter(e.target.value)}
                className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-1.5 text-xs text-white focus:outline-none focus:border-emerald-500 appearance-none"
              >
                <option value="">Tous les champs</option>
                {orgCustomFields.map(f => (
                  <option key={f.id} value={f.id}>{f.label}</option>
                ))}
              </select>
            )}
            </div>
            )}
          </div>

          {visibleTeamFiltered.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500 space-y-4">
              <Users className="w-16 h-16 opacity-20" />
              <p>{searchQuery ? "Aucun membre ne correspond à cette recherche." : "Aucun membre n'a encore été ajouté."}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 gap-3">
            {visibleTeamFiltered.map(s => {
              const cat = categories.find(c => c.id === s.categoryId);
              const isShared = s.orgId && s.orgId !== currentOrgId;
              const hasAutonomousAccess = s.emailConfirmed || isShared;

              const isSelected = selectedTeamIds.has(s.id);
              return (
                <div key={s.id} className={`group bg-[#0A0B0E]/80 backdrop-blur-sm border rounded-xl px-5 py-4 flex flex-col xl:flex-row xl:items-center justify-between gap-4 transition-all duration-300 hover:border-emerald-500/30 hover:shadow-lg hover:shadow-emerald-500/5 hover:-translate-y-0.5 ${isSelected ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-[#2D3139]'}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    <label className="flex items-center cursor-pointer flex-shrink-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleTeamSelection(s.id)}
                        className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500/20"
                      />
                    </label>
                    <div className="relative">
                      {s.photo
                        ? <img src={s.photo} className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-[#1A1D23] shadow-md" alt="" />
                        : <div className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center text-lg font-bold text-white shadow-md border-2 border-[#1A1D23]" style={{ backgroundColor: s.color || '#6366f1' }}>{s.name.charAt(0).toUpperCase()}</div>
                      }
                      <span className="absolute bottom-0 right-0 w-3.5 h-3.5 border-2 border-[#0A0B0E] rounded-full" style={{ backgroundColor: s.color || '#6366f1' }}></span>
                      {(messages || []).some(m => m.teamId === s.id && !m.read && m.fromTeam) && (
                        <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-orange-500 rounded-full animate-orange-blink border-2 border-[#0A0B0E]" title="Nouveau message non lu" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="text-white font-bold text-base truncate tracking-wide flex items-center gap-2">
                        {s.name}
                        {isShared && (
                          <span className="text-[9px] font-black text-purple-400 bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 rounded uppercase tracking-wider">Freelance 🌐</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 flex-wrap mt-1">
                        {/* Primary role = category */}
                        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded uppercase tracking-wider">
                          <Shield className="w-3 h-3" />
                          {cat?.name || 'Non assigné'}
                        </span>
                        {/* Custom roles */}
                        {(s.roles || []).filter(Boolean).map((r: string) => (
                          <span key={r} className="text-[10px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded uppercase tracking-wider">
                            {r}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center flex-wrap gap-2 flex-shrink-0 pt-3 xl:pt-0 border-t border-[#2D3139] xl:border-t-0 mt-2 xl:mt-0">
                    {/* Team access token actions */}
                    {hasAutonomousAccess ? (
                      isSuperAdmin ? (
                        // Superadmin can always see the token link, even for autonomous accounts
                        s.teamToken ? (
                          <div className="flex gap-1 bg-[#1A1D23] p-1 rounded-lg border border-[#2D3139]">
                            <button
                              onClick={(e) => { e.stopPropagation(); showExistingTeamLink(s); }}
                              className="flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 transition-colors px-2.5 py-1.5 rounded text-xs font-semibold"
                            ><Link className="w-3.5 h-3.5"/> Lien Accès</button>
                            <button
                              onClick={(e) => { e.stopPropagation(); revokeTeamToken(s); }}
                              className="flex items-center gap-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors px-2.5 py-1.5 rounded text-xs font-semibold"
                            ><Link2Off className="w-3.5 h-3.5"/> Révoquer</button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); generateTeamLink(s); }}
                            className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 bg-[#1A1D23] hover:bg-emerald-500/10 border border-[#2D3139] hover:border-emerald-500/30 transition-colors px-3 py-2 rounded-lg text-xs font-bold"
                          ><Link className="w-3.5 h-3.5"/> Générer Accès (Superadmin)</button>
                        )
                      ) : (
                        <span className="text-[10px] font-bold text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2.5 py-1.5 rounded-lg flex items-center gap-1.5">
                          <Shield className="w-3.5 h-3.5"/> Compte Autonome
                        </span>
                      )
                    ) : s.teamToken ? (
                      <div className="flex gap-1 bg-[#1A1D23] p-1 rounded-lg border border-[#2D3139]">
                        <button
                          onClick={(e) => { e.stopPropagation(); showExistingTeamLink(s); }}
                          className="flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 transition-colors px-2.5 py-1.5 rounded text-xs font-semibold"
                        ><Link className="w-3.5 h-3.5"/> Lien Accès</button>
                        <button
                          onClick={(e) => { e.stopPropagation(); revokeTeamToken(s); }}
                          className="flex items-center gap-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors px-2.5 py-1.5 rounded text-xs font-semibold"
                        ><Link2Off className="w-3.5 h-3.5"/> Révoquer</button>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); generateTeamLink(s); }}
                        className="flex items-center gap-1.5 text-emerald-400 hover:text-emerald-300 bg-[#1A1D23] hover:bg-emerald-500/10 border border-[#2D3139] hover:border-emerald-500/30 transition-colors px-3 py-2 rounded-lg text-xs font-bold"
                      ><Link className="w-3.5 h-3.5"/> Générer Accès</button>
                    )}

                    {/* Dissocier QR si un bracelet est linké */}
                    {s.linkedWalletQr && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleUnlinkWallet(s.id, s.name); }}
                        className="flex items-center gap-1.5 text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/40 transition-colors px-3 py-2 rounded-lg text-xs font-bold"
                        title={`Dissocier le QR lié à ${s.name}`}
                      >
                        <Link2Off className="w-3.5 h-3.5" /> Dissocier QR
                      </button>
                    )}

                    <div className="flex items-center gap-1 bg-[#1A1D23] p-1 rounded-lg border border-[#2D3139]">
                      {isSuperAdmin && (
                        <button 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setImpersonatedTeamId(s.id); 
                          }} 
                          className="text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 transition-colors p-2 text-sm rounded" 
                          title="Mode Miroir — Superadmin uniquement"
                        >
                          <Eye className="w-4 h-4"/>
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); setPromptVal(""); setPromptModal({ title: `Message pour ${s.name}`, onConfirm: (val) => { const senderName = currentUser.role === 'admin' ? `Admin : ${currentUser.email}` : `Manager : ${currentUser.email}`; addMessage(s.id, val, senderName); } }); }} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors p-2 text-sm rounded" title="Envoyer Message">
                        <MessageSquare className="w-4 h-4"/>
                      </button>
                      {s.emailConfirmed && !isSuperAdmin && s.id !== currentUser.teamId ? (
                        <button disabled className="text-slate-600 cursor-not-allowed p-2 text-sm rounded" title="Compte vérifié : Modification réservée au Superadmin">
                          <Lock className="w-4 h-4 text-slate-600"/>
                        </button>
                      ) : (
                        <button onClick={(e) => { e.stopPropagation(); handleEdit(s); }} className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 transition-colors p-2 text-sm rounded" title="Modifier">
                          <Edit2 className="w-4 h-4"/>
                        </button>
                      )}
                      {isSuperAdmin ? (
                        /* Superadmin : toujours supprimer définitivement */
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmModal({
                              message: `Supprimer définitivement ${s.name} ?`,
                              onConfirm: () => deleteTeam(s.id)
                            });
                          }}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors p-2 text-sm rounded"
                          title="Supprimer (Superadmin)"
                        >
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      ) : s.emailConfirmed || isShared ? (
                        /* Compte autonome ou freelance : retirer de l'org uniquement */
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmModal({
                              message: `Retirer ${s.name} de votre organisation ? Son compte restera intact.`,
                              onConfirm: () => {
                                if (isShared) {
                                  removeSharedTeamLink(s.id, currentOrgId || '');
                                } else {
                                  updateTeam(s.id, { orgId: null });
                                }
                              }
                            });
                          }}
                          className="text-slate-400 hover:text-orange-400 hover:bg-orange-500/10 transition-colors p-2 text-sm rounded"
                          title="Retirer de l'organisation (le compte reste intact)"
                        >
                          <Link2Off className="w-4 h-4"/>
                        </button>
                      ) : (
                        /* Compte standard : suppression définitive */
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmModal({
                              message: `Supprimer ${s.name} ?`,
                              onConfirm: () => deleteTeam(s.id)
                            });
                          }}
                          className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors p-2 text-sm rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4"/>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Link modal */}
      {linkModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] p-8 rounded-2xl w-full max-w-md shadow-2xl animate-in zoom-in-95">
            <div className="w-12 h-12 bg-indigo-500/20 text-indigo-400 rounded-2xl flex items-center justify-center mb-6 border border-indigo-500/30">
              <Link className="w-6 h-6" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Lien d'accès Mobile</h3>
            <p className="text-slate-400 text-sm mb-6">Team : <span className="text-white font-medium bg-[#2D3139] px-2 py-1 rounded ml-1">{linkModal.teamName}</span></p>
            
            <div className="bg-[#0A0B0E] border border-[#2D3139] rounded-xl p-4 mb-6 relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
              <a href={linkModal.link} target="_blank" rel="noreferrer" className="text-indigo-400 text-xs hover:underline font-mono break-all">{linkModal.link}</a>
            </div>
            
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { navigator.clipboard.writeText(linkModal.link); }}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-all shadow-lg shadow-indigo-500/20 text-sm font-bold flex items-center justify-center gap-2"
              >📋 Copier le lien</button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`Salut ! Voici ton lien d'accès FestPlanner :\n\n${linkModal.link}`)}`}
                target="_blank" rel="noreferrer"
                className="w-full py-3 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-xl transition-all shadow-lg shadow-[#25D366]/20 text-sm font-bold text-center flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12c0 1.76.46 3.42 1.25 4.89L2 22l5.24-1.21c1.42.74 3.03 1.15 4.75 1.15 5.52 0 10-4.48 10-10S17.51 2 11.99 2zm5.72 14.19c-.23.64-1.34 1.22-1.85 1.31-.47.08-1.07.13-3.41-.84-2.82-1.17-4.64-4.04-4.78-4.23-.14-.19-1.14-1.52-1.14-2.9 0-1.38.71-2.06.97-2.34.24-.26.54-.33.72-.33.18 0 .37 0 .52.01.16.01.37-.06.57.42.21.5.72 1.76.78 1.89.06.13.1.28.02.42-.08.14-.13.23-.26.38-.13.15-.27.32-.39.45-.13.14-.27.29-.11.57.16.28.71 1.18 1.53 1.92 1.05.95 1.94 1.25 2.22 1.38.28.13.44.11.6-.07.16-.18.69-.8 87-1.08.18-.28.36-.23.61-.14.25.09 1.59.75 1.86.88.27.13.45.19.52.3.07.11.07.64-.16 1.28z" />
                </svg>
                Partager sur WhatsApp
              </a>
              <button onClick={() => setLinkModal(null)} className="py-3 px-4 bg-transparent hover:bg-[#1A1D23] border border-[#2D3139] text-white rounded-xl transition-colors text-sm font-medium">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] p-8 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in-95 text-center">
            <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-500/20">
              <AlertTriangle className="w-8 h-8"/>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Confirmation</h3>
            <p className="text-slate-400 mb-8">{confirmModal.message}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmModal(null)} className="flex-1 py-3 bg-[#1A1D23] hover:bg-[#22262E] text-white rounded-xl transition-colors text-sm font-medium">Annuler</button>
              <button onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }} className="flex-1 py-3 bg-red-600 hover:bg-red-500 text-white rounded-xl transition-all shadow-lg shadow-red-500/20 text-sm font-bold">Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt modal (messages) */}
      {promptModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[110] animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] p-8 rounded-2xl w-full max-w-sm shadow-2xl animate-in zoom-in-95">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-500/20 text-blue-400 rounded-xl">
                <MessageSquare className="w-6 h-6"/>
              </div>
              <h4 className="text-white font-bold text-lg">{promptModal.title}</h4>
            </div>
            <textarea
              value={promptVal}
              onChange={e => setPromptVal(e.target.value)}
              placeholder={promptModal.placeholder || "Écrivez votre message ici..."}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/50 mb-6 h-32 resize-none placeholder-slate-600"
            />
            <div className="flex gap-3">
              <button onClick={() => setPromptModal(null)} className="flex-1 py-3 bg-[#1A1D23] hover:bg-[#22262E] text-white rounded-xl transition-colors text-sm font-medium">Annuler</button>
              <button onClick={() => { if (promptVal.trim()) { promptModal.onConfirm(promptVal); setPromptModal(null); setPromptVal(""); } }} className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl transition-all shadow-lg shadow-blue-500/20 text-sm font-bold">Envoyer le message</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invite Modal ─────────────────────────────────────────────── */}
      {inviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Link className="w-4 h-4 text-indigo-400" />
                <h3 className="font-bold text-white">Lien d'invitation Team</h3>
              </div>
              <button onClick={() => setInviteModal(false)} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-[#2D3139] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-slate-400 text-sm">Sélectionnez une catégorie puis partagez le lien. Le team pourra s'inscrire et sera mis en attente de validation.</p>
              {currentUser.role === 'admin' && (
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Catégorie</label>
                  <select
                    value={inviteCategoryId}
                    onChange={e => setInviteCategoryId(e.target.value)}
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 transition-all appearance-none"
                  >
                    <option value="">Sélectionnez une catégorie...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {inviteCategoryId && (
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">Lien à partager</label>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/?invite=${inviteCategoryId}`}
                      className="flex-1 bg-[#0A0B0E] border border-indigo-500/40 rounded-xl px-3 py-2.5 text-indigo-300 text-sm font-mono focus:outline-none"
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}/?invite=${inviteCategoryId}`)}
                      className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition-colors text-sm"
                    >Copier</button>
                  </div>
                  <p className="text-slate-600 text-xs mt-2">⚠️ Ce lien permet à n'importe qui de soumettre une demande. Validez manuellement chaque membre.</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-[#2D3139]">
              <button onClick={() => setInviteModal(false)} className="w-full py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-white font-medium rounded-xl text-sm transition-colors">Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Pending approvals (fixed bottom-right badge panel) ────── */}
      {(() => {
        const pendingTeam = allTeam.filter(s => s.status === 'pending' && s.orgId === currentOrgId && !s.isArtist);
        if (pendingTeam.length === 0) return null;
        return (
          <div className="fixed bottom-4 right-4 z-40">
            <div className="bg-[#14161B] border border-amber-500/40 rounded-2xl shadow-2xl w-80 overflow-hidden">
              <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3 flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                  <span className="text-amber-300 font-bold text-sm">{pendingTeam.length} en attente d'approbation</span>
                </div>
                <button
                  onClick={() => {
                    if (!window.confirm(`Approuver les ${pendingTeam.length} demandes en attente ?`)) return;
                    pendingTeam.forEach(s => updateTeam(s.id, { status: 'approved' }));
                  }}
                  className="text-[10px] font-bold text-amber-400 hover:text-amber-300 bg-amber-500/20 hover:bg-amber-500/30 px-2 py-1 rounded-lg transition-colors whitespace-nowrap"
                >
                  Tout approuver
                </button>
              </div>
              <div className="max-h-72 overflow-y-auto custom-scrollbar">
                {pendingTeam.map(s => {
                  const cat = categories.find(c => c.id === s.categoryId);
                  return (
                    <div key={s.id} className="p-3 border-b border-[#2D3139] last:border-b-0">
                      <div className="flex items-center gap-2 mb-2">
                        {s.photo ? (
                          <img src={s.photo} alt={s.name} className="w-8 h-8 rounded-full object-cover border border-[#2D3139]" />
                        ) : (
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: s.color }}>
                            {s.name[0]?.toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{s.name}</p>
                          <p className="text-slate-500 text-[11px] truncate">{s.phone}{s.email ? ` · ${s.email}` : ''}</p>
                          {cat && <p className="text-indigo-400 text-[10px] font-medium">{cat.name}</p>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => { if (confirm(`Approuver ${s.name} ?`)) updateTeam(s.id, { status: 'approved' }); }}
                          className="flex-1 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 py-1.5 rounded-lg transition-colors"
                        >✓ Approuver</button>
                        <button
                          onClick={() => { if (confirm(`Rejeter et supprimer ${s.name} ?`)) deleteTeam(s.id); }}
                          className="flex-1 text-xs font-medium text-slate-400 hover:text-red-400 bg-[#1A1D23] hover:bg-red-500/10 border border-[#2D3139] hover:border-red-500/30 py-1.5 rounded-lg transition-all"
                        >✕ Rejeter</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Freelancer Discovery Modal */}
      {findFreelancersOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-[120] animate-in fade-in">
          <div className="bg-[#14161B] border border-[#2D3139] rounded-3xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95">
            {/* Modal Header */}
            <div className="p-6 border-b border-[#2D3139] flex justify-between items-start">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Globe className="text-purple-400 w-5 h-5 animate-pulse" /> Trouver des Teams Externes 🌐
                </h3>
                <p className="text-xs text-slate-400 mt-1">
                  Recherchez et ajoutez des freelances vérifiés de la plateforme Woodstock Teams à votre pool d'intervenants.
                </p>
              </div>
              <button 
                onClick={() => setFindFreelancersOpen(false)}
                className="p-2 bg-[#1A1D23] hover:bg-[#22262E] text-slate-400 hover:text-white rounded-xl border border-[#2D3139] transition-colors"
              >✕</button>
            </div>

            {/* Modal Filters */}
            <div className="p-6 bg-[#0A0B0E]/60 border-b border-[#2D3139] grid grid-cols-1 sm:grid-cols-4 gap-3.5">
              {/* Text Search */}
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Rechercher</label>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                  <input 
                    type="text" 
                    value={freelanceSearch} 
                    onChange={e => setFreelanceSearch(e.target.value)} 
                    placeholder="Nom du team..." 
                    className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl pl-9 pr-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>

              {/* Specialty filter */}
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Spécialité</label>
                <select 
                  value={freelanceSpecialty} 
                  onChange={e => setFreelanceSpecialty(e.target.value)} 
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="">Toutes spécialités</option>
                  {['Bar', 'Cashless', 'Accueil', 'Logistique', 'Électricité', 'Son & Lumière', 'Sécurité'].map(skill => (
                    <option key={skill} value={skill}>{skill}</option>
                  ))}
                </select>
              </div>

              {/* Geographical Zone */}
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Zone Géo</label>
                <input 
                  type="text" 
                  value={freelanceZone} 
                  onChange={e => setFreelanceZone(e.target.value)} 
                  placeholder="Ex: Tunis, Europe..." 
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              {/* Max rate */}
              <div>
                <label className="block text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1.5">Tarif max : {freelanceMaxRate} TND/EUR</label>
                <input 
                  type="range" 
                  min={10} 
                  max={1000} 
                  step={10} 
                  value={freelanceMaxRate} 
                  onChange={e => setFreelanceMaxRate(parseInt(e.target.value))} 
                  className="w-full accent-purple-500 h-1 bg-[#2D3139] rounded-lg appearance-none cursor-pointer mt-3"
                />
              </div>
            </div>

            {/* Modal Body / Freelancer List */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
              {(() => {
                const publicFreelancers = allTeam.filter(s => 
                  s.emailConfirmed === true && 
                  s.isProfileVisible === true
                  // s.orgId !== currentOrgId &&
                  // !visibleTeam.some(vs => vs.id === s.id)
                );

                const filteredFreelancers = publicFreelancers.filter(s => {
                  if (freelanceSearch.trim() && !s.name.toLowerCase().includes(freelanceSearch.toLowerCase())) {
                    return false;
                  }
                  if (freelanceSpecialty && !(s.publicSkills || []).includes(freelanceSpecialty)) {
                    return false;
                  }
                  if (freelanceZone.trim() && !((s.geographicalZone || '').toLowerCase().includes(freelanceZone.toLowerCase()))) {
                    return false;
                  }
                  if (s.indicativeDailyRate && s.indicativeDailyRate > freelanceMaxRate) {
                    return false;
                  }
                  return true;
                });

                if (filteredFreelancers.length === 0) {
                  return (
                    <div className="text-center py-16 space-y-3">
                      <div className="w-12 h-12 bg-slate-800/50 rounded-full flex items-center justify-center mx-auto text-slate-500 border border-[#2D3139]/40">
                        <Users className="w-5 h-5" />
                      </div>
                      <p className="text-slate-400 text-sm italic">Aucun freelance public correspondant à votre recherche.</p>
                      <p className="text-xs text-slate-500 max-w-sm mx-auto">
                        Ajustez vos filtres de recherche ou de tarif pour afficher d'autres intervenants disponibles.
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredFreelancers.map(s => (
                      <div key={s.id} className="bg-[#0A0B0E]/60 border border-[#2D3139] rounded-2xl p-5 flex flex-col justify-between gap-4 hover:border-purple-500/30 hover:shadow-lg hover:shadow-purple-500/5 transition-all">
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            {s.photo ? (
                              <img src={s.photo} alt={s.name} className="w-10 h-10 rounded-full object-cover border border-[#2D3139]" />
                            ) : (
                              <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: s.color }}>
                                {s.name[0]?.toUpperCase()}
                              </div>
                            )}
                            <div>
                              <h4 className="font-bold text-white text-sm">{s.name}</h4>
                              <p className="text-[10px] text-slate-400">{s.geographicalZone || 'Toute zone'}</p>
                            </div>
                          </div>

                          {/* Bio description */}
                          {s.publicDescription && (
                            <p className="text-slate-300 text-xs leading-relaxed italic bg-[#14161B]/40 p-2.5 border border-[#2D3139]/30 rounded-xl">
                              "{s.publicDescription}"
                            </p>
                          )}

                          {/* Specialties / Skills Tags */}
                          {s.publicSkills && s.publicSkills.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {s.publicSkills.map(skill => (
                                <span key={skill} className="bg-purple-500/10 border border-purple-500/25 text-purple-300 text-[9px] font-semibold px-2 py-0.5 rounded-full">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Footer: Rate + Link Action */}
                        <div className="pt-3 border-t border-[#2D3139]/40 flex items-center justify-between">
                          <div>
                            <p className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Tarif Indicatif</p>
                            <p className="text-sm font-black text-purple-400">{s.indicativeDailyRate || '--'} {s.proposedCurrency || 'TND'}<span className="text-[10px] font-normal text-slate-500">/jour</span></p>
                          </div>
                          <button
                            onClick={async () => {
                              const linkError = await addSharedTeamLink(s.id, currentOrgId || '');
                              if (linkError) {
                                alert(`Impossible d'ajouter ${s.name}.\n\nErreur : ${linkError}`);
                              } else {
                                alert(`${s.name} a été ajouté à votre liste d'intervenants ! ➕`);
                              }
                            }}
                            className="bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs px-3.5 py-2 rounded-xl transition-all shadow-md shadow-purple-600/10 flex items-center gap-1.5"
                          >
                            <Plus className="w-3.5 h-3.5" /> Ajouter
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-[#0A0B0E]/60 border-t border-[#2D3139] flex justify-end">
              <button 
                onClick={() => setFindFreelancersOpen(false)}
                className="py-2.5 px-5 bg-transparent hover:bg-[#1A1D23] border border-[#2D3139] text-white rounded-xl transition-all text-xs font-semibold"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wallet QR Scanner Modal */}
      {walletScanOpen && (
        <div className="fixed inset-0 bg-[#0A0B0E]/95 backdrop-blur-sm z-[130] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-[#2D3139]">
            <span className="font-bold text-white text-lg">Scanner un QR Code</span>
            <button onClick={() => setWalletScanOpen(false)} className="p-2 bg-[#1A1D23] rounded-xl text-slate-400 hover:text-white border border-[#2D3139]">✕</button>
          </div>
          <div className="flex-1 relative">
            <Scanner onScan={(result) => result && handleWalletScan(result[0].rawValue)} onError={() => {}} />
          </div>
          <div className="p-4 border-t border-[#2D3139]">
            <p className="text-[9px] text-slate-500 text-center">Scannez le bracelet ou carte du team pour le lier à son compte</p>
          </div>
        </div>
      )}

      {/* Wallet Team Picker Modal */}
      {scannedWalletValue && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[135] flex items-center justify-center p-4" onClick={() => setScannedWalletValue(null)}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-[#2D3139] flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-white">Assigner le QR Code</h3>
                <p className="text-[10px] text-purple-400 font-mono mt-1 truncate max-w-[250px]">{scannedWalletValue}</p>
              </div>
              <button onClick={() => setScannedWalletValue(null)} className="p-2 hover:bg-[#1A1D23] rounded-lg text-slate-400"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-3 border-b border-[#2D3139]">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input type="text" placeholder="Rechercher un team..." value={walletTeamSearch} onChange={e => setWalletTeamSearch(e.target.value)} autoFocus
                  className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-lg pl-8 pr-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-purple-500" />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto custom-scrollbar p-2 space-y-1">
              {walletFiltered.length === 0 ? (
                <p className="text-[10px] text-slate-500 text-center py-4">Aucun team trouvé</p>
              ) : (
                walletFiltered.map(s => (
                  <button key={s.id} onClick={() => handleAssignWallet(s.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#1A1D23] transition-colors text-left">
                    <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-[9px] font-bold text-purple-400 shrink-0">{s.name[0]}</div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-200 truncate">{s.name}</div>
                      <div className="text-[9px] text-slate-500 truncate">{s.linkedWalletQr ? '🔗 QR déjà lié' : 'Aucun QR lié'}</div>
                    </div>
                    <CheckCircle className="w-4 h-4 text-slate-600" />
                  </button>
                ))
              )}
            </div>
            <div className="p-3 border-t border-[#2D3139] bg-[#0A0B0E] rounded-b-2xl">
              <button onClick={() => setScannedWalletValue(null)} className="w-full py-2.5 bg-[#1A1D23] hover:bg-[#2D3139] text-slate-300 rounded-lg text-xs font-bold">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Action Bar */}
      {hasSelection && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#1A1D23]/95 backdrop-blur-sm border-t border-emerald-500/30 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 flex-wrap">
            <span className="text-white font-semibold text-sm mr-2">
              {selectedTeamIds.size} sélectionné{selectedTeamIds.size > 1 ? 's' : ''}
            </span>

            {/* Bulk Delete */}
            <button
              onClick={bulkDelete}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Supprimer
            </button>

            {/* Bulk Message */}
            <button
              onClick={bulkSendMessage}
              className="flex items-center gap-1.5 px-3 py-2 bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 rounded-lg text-xs font-medium transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Message
            </button>

            {/* Bulk Reassign Category */}
            <button
              onClick={() => {
                setBulkCategoryId(categories[0]?.id || '');
                setShowBulkCategoryModal(true);
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg text-xs font-medium transition-colors"
            >
              <FolderKanban className="w-3.5 h-3.5" />
              Changer catégorie
            </button>

            {/* Bulk Manage Custom Roles */}
            <button
              onClick={() => {
                setBulkCustomRoleAction('add');
                setBulkCustomRoleValue('');
                setShowBulkCustomRoleModal(true);
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-500/30 text-sky-400 rounded-lg text-xs font-medium transition-colors"
            >
              <Shield className="w-3.5 h-3.5" />
              Gérer rôles perso.
            </button>

            {/* Bulk Approve (only if pending team selected) */}
            {hasPendingSelected && (
              <button
                onClick={bulkApprove}
                className="flex items-center gap-1.5 px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium transition-colors"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                Approuver
              </button>
            )}

            {/* Deselect */}
            <button
              onClick={clearSelection}
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-700/50 hover:bg-slate-700 border border-slate-600 text-slate-300 rounded-lg text-xs font-medium transition-colors ml-auto"
            >
              <X className="w-3.5 h-3.5" />
              Désélectionner
            </button>
          </div>
        </div>
      )}

      {/* Bulk Category Reassign Modal */}
      {showBulkCategoryModal && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-semibold mb-1">Changer la catégorie</h3>
            <p className="text-slate-400 text-sm mb-4">
              {selectedTeamIds.size} membre(s) seront déplacés vers la catégorie sélectionnée.
            </p>
            <select
              value={bulkCategoryId}
              onChange={e => setBulkCategoryId(e.target.value)}
              className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all appearance-none mb-4"
            >
              {categories.filter(c => c.orgId === currentOrgId).map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowBulkCategoryModal(false)}
                className="flex-1 px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={bulkReassignCategory}
                disabled={!bulkCategoryId}
                className="flex-1 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors"
              >
                Déplacer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Custom Role Modal */}
      {showBulkCustomRoleModal && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-[#14161B] border border-[#2D3139] rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-white font-semibold mb-1">Gérer les rôles personnalisés</h3>
            <p className="text-slate-400 text-sm mb-4">
              {selectedTeamIds.size} membre(s) — ajouter ou retirer un rôle personnalisé.
            </p>

            {/* Add / Remove toggle */}
            <div className="flex gap-2 p-1 bg-[#0A0B0E] rounded-xl mb-4">
              <button
                onClick={() => setBulkCustomRoleAction('add')}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${bulkCustomRoleAction === 'add' ? 'bg-emerald-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
              >
                + Ajouter
              </button>
              <button
                onClick={() => setBulkCustomRoleAction('remove')}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all ${bulkCustomRoleAction === 'remove' ? 'bg-red-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
              >
                − Retirer
              </button>
            </div>

            {/* Input for new role name (add mode) */}
            {bulkCustomRoleAction === 'add' ? (
              <input
                type="text"
                value={bulkCustomRoleValue}
                onChange={e => setBulkCustomRoleValue(e.target.value)}
                placeholder="Ex: VIP Host, Runner..."
                className="w-full bg-[#0A0B0E] border border-[#2D3139] rounded-xl px-4 py-3 text-white focus:outline-none focus:border-emerald-500 transition-all placeholder-slate-600 mb-4"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') bulkManageCustomRole(); }}
              />
            ) : (
              /* Select from existing roles (remove mode) */
              distinctSelectedCustomRoles.length > 0 ? (
                <>
                  <p className="text-[10px] text-slate-500 mb-2 uppercase tracking-wider">Sélectionner le rôle à retirer :</p>
                  <div className="max-h-40 overflow-y-auto custom-scrollbar space-y-1 mb-4">
                    {distinctSelectedCustomRoles.map(role => (
                      <button
                        key={role}
                        onClick={() => setBulkCustomRoleValue(role === bulkCustomRoleValue ? '' : role)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                          bulkCustomRoleValue === role
                            ? 'bg-red-500/20 border border-red-500/30 text-red-400'
                            : 'bg-[#0A0B0E] border border-[#2D3139] text-amber-400 hover:border-red-500/30'
                        }`}
                      >
                        {role} {bulkCustomRoleValue === role && '✓'}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-slate-500 text-xs text-center py-4 mb-4">Aucun rôle personnalisé dans la sélection.</p>
              )
            )}

            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowBulkCustomRoleModal(false)}
                className="flex-1 px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={bulkManageCustomRole}
                disabled={!bulkCustomRoleValue.trim()}
                className={`flex-1 px-4 py-2.5 disabled:opacity-50 text-white rounded-xl text-sm font-medium transition-colors ${
                  bulkCustomRoleAction === 'add'
                    ? 'bg-emerald-500 hover:bg-emerald-600'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {bulkCustomRoleAction === 'add' ? 'Ajouter à tous' : 'Retirer de tous'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
